/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { Contenido, Etapa, Seccion, TipoDeCorreccion } from "@gestiondoc/shared";
import {
  densidadDe,
  diaLocal,
  ETAPAS,
  formatearRevision,
  NOMBRE_ETAPA,
  orientacionDe,
} from "@gestiondoc/shared";
import type { ReactNode } from "react";
import { BloqueDeHoja } from "./bloques.js";
import { enLinea, resaltadosDe } from "./enLinea.js";

/**
 * La hoja ISO (ADR-03): el documento completo a partir del contenido por bloques y las firmas.
 *
 * El formato es el de los documentos reales de SMG, copiado de GES-PRG-009 Rev. 01 (Programa
 * de control de químicos, marzo 2025), el primer documento que entregó Calidad el 2026-09-22:
 * encabezado en tabla con el logo a la izquierda y el título arriba de la grilla
 * Código / Revisión / Fecha / Página; cuerpo en Times a 12 pt, justificado, con las secciones
 * numeradas en mayúsculas; y al pie el bloque legal de documento controlado, con la línea del
 * sistema y la de confidencialidad.
 *
 * Un solo componente para dos usos. La web lo monta en vivo como vista previa mientras se
 * redacta (`modo="pantalla"`: dibuja su encabezado y su pie, y "1 de ?" porque en pantalla el
 * total no se sabe). El worker lo renderiza a texto con `renderToStaticMarkup` y lo imprime con
 * Chromium (`modo="impresion"`: sin encabezado ni pie, que los pone Chromium en cada página con
 * `encabezadoHtml` y `pieHtml` de `./impresion`, donde sí resuelve "Página X de Y").
 *
 * Los bloques del cuerpo van en `./bloques.tsx` y el texto corrido en `./enLinea.tsx`.
 *
 * No usa hooks ni estado: tiene que poder renderizarse en el servidor sin DOM.
 */

export type FirmaDeHoja = {
  etapa: Etapa;
  nombre: string | null;
  puesto: string | null;
  firmadoEn: Date | null;
  /** URL de la imagen de firma estampada, o null si está pendiente. */
  imagenUrl: string | null;
  idFirma: string | null;
};

/** Una fila de "Control de las actualizaciones": una revisión, quién la pidió y qué cambió. */
type EntradaDeHistorial = {
  revision: number;
  fecha: Date | null;
  quien: string;
  puesto: string | null;
  descripcion: string;
};

export type DatosDeHoja = {
  codigo: string;
  nombre: string;
  revision: number;
  /** Fecha de aprobación; null mientras es borrador (se muestra la de hoy). */
  fecha: Date | null;
  contenido: Contenido;
  firmas: FirmaDeHoja[];
  /** El ID del sello, cuando ya se selló. */
  idSello: string | null;
  /** Marca de agua BORRADOR (vista previa PDF sin sello). */
  borrador: boolean;
  /** El logo de SMG del encabezado. Cada lado lo resuelve como `urlDeArchivo`. */
  logoUrl: string | null;
  /**
   * Cómo llegar a la imagen de un archivo: la web pasa una ruta de la API (`/archivos/{id}`) y
   * el worker un `data:` con los bytes, porque Chromium no tiene sesión.
   */
  urlDeArchivo: (archivoId: string) => string;
  /** Los flujogramas ya renderizados a SVG, por id de bloque. Sin SVG sale un marcador. */
  svgs?: Record<string, string>;
  /** El historial de revisiones, para la sección automática "Control de las actualizaciones". */
  historial?: EntradaDeHistorial[];
  /** La línea del sistema en el pie legal. SMG imprime la de inocuidad. */
  sistema?: string;
  /** Fase 3b: documento informativo, publicado sin ruta de firmas ni sello. */
  informativo?: boolean;
  /**
   * Fase 4: las marcas del revisor que se pintan sobre el texto (pantalla 09). Solo en pantalla;
   * el PDF no las lleva.
   */
  resaltados?: ResaltadoDeHoja[];
};

/** Un tramo marcado: párrafo, desplazamientos sobre su texto (`textoDe`) y cómo se pinta. */
export type ResaltadoDeHoja = {
  bloqueId: string;
  inicio: number;
  fin: number;
  numero: number;
  tipo: TipoDeCorreccion;
  /** La marca elegida en el panel: se destaca. */
  activo?: boolean;
  /** Un tramo más de una marca que abarca varios párrafos: se pinta, pero el número va solo en el primero. */
  sinNumero?: boolean;
  /** Lo que se ve al pasar el ratón. */
  titulo?: string;
};

const SISTEMA_POR_DEFECTO = "Sistema de Gestión de la Inocuidad de los Alimentos";

type Modo = "pantalla" | "impresion";

export function HojaISO({
  datos,
  modo = "pantalla",
  pagina = "1 de ?",
}: {
  datos: DatosDeHoja;
  modo?: Modo;
  /** La casilla Página del encabezado en pantalla; el PDF de una hoja llena pone "1 de N". */
  pagina?: ReactNode;
}) {
  return (
    <article
      className="hoja"
      data-modo={modo}
      data-borrador={datos.borrador ? "true" : "false"}
      data-orientacion={orientacionDe(datos.contenido)}
      data-densidad={densidadDe(datos.contenido)}
    >
      {modo === "pantalla" && <Encabezado datos={datos} pagina={pagina} />}
      <main className="hoja-cuerpo">
        {datos.contenido.content.map((s, i) => (
          <SeccionDeHoja
            key={s.attrs.id}
            seccion={s}
            numero={i + 1}
            datos={datos}
            // Un documento de una sola sección (un formato, un comunicado) no la titula: el
            // título ya está en el encabezado, y "1. CONTENIDO DEL REGISTRO" es ruido.
            sinTitulo={datos.contenido.content.length === 1}
          />
        ))}
      </main>
      {datos.firmas.length > 0 && <PieDeFirmas firmas={datos.firmas} />}
      {modo === "pantalla" && <BloqueLegal datos={datos} />}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Encabezado y pie: los comparte la impresión (./impresion)
// ---------------------------------------------------------------------------

/** El encabezado de cada página. `pagina` es texto porque en el PDF lo rellena Chromium. */
export function Encabezado({ datos, pagina }: { datos: DatosDeHoja; pagina: ReactNode }) {
  return (
    <header className="hoja-encabezado">
      <table className="hoja-encabezado-tabla">
        <tbody>
          <tr>
            <td rowSpan={3} className="hoja-logo">
              {datos.logoUrl ? (
                <img src={datos.logoUrl} alt="SMG · Creamos sabores y soluciones" />
              ) : (
                <span className="hoja-logo-marcador">SMG</span>
              )}
            </td>
            <td colSpan={4} className="hoja-titulo">
              {datos.nombre}
            </td>
          </tr>
          <tr>
            <th>Código:</th>
            <td className="hoja-dato">{datos.codigo}</td>
            <th>Revisión:</th>
            <td className="hoja-dato hoja-rev">{formatearRevision(datos.revision)}</td>
          </tr>
          <tr>
            <th>Fecha:</th>
            <td className="hoja-dato">{mesYAnio(datos.fecha ?? new Date())}</td>
            <th>Página:</th>
            <td className="hoja-dato">{pagina}</td>
          </tr>
        </tbody>
      </table>
    </header>
  );
}

/** La línea del sello, la única que no está en el documento de Word: es la evidencia digital. */
function leyenda(datos: DatosDeHoja): string {
  if (datos.idSello) return `Sello ID ${datos.idSello}`;
  if (datos.informativo && !datos.borrador)
    return "Documento informativo: publicado sin ruta de firmas";
  // Vigente sin sello de la app: importada de CSGestión (sus firmas están en el papel), o el
  // worker todavía no la sella.
  if (!datos.borrador) return "Sin sello digital";
  return "Sin sello: borrador";
}

/** El pie legal de documento controlado, con el texto exacto que SMG imprime. */
export function BloqueLegal({ datos }: { datos: DatosDeHoja }) {
  return (
    <footer className="hoja-legal">
      <p className="hoja-legal-titulo">
        Documento Controlado - Propiedad de <span className="hoja-azul">SMG Comercial, S.A.</span>
      </p>
      <p>
        Toda copia impresa de este documento, a excepción de las firmadas y selladas en el original,
        deben ser consideradas como no controladas. El emisor de este documento es el responsable de
        asegurar que las copias impresas estén actualizadas y aprobadas antes de su uso.
      </p>
      <p>
        Se advierte al lector, que el contenido de este documento puede ser objeto de modificaciones
        según sea necesario y posteriores a la fecha de revisión sin que se le pueda informar
        directamente de tales cambios. En tal caso, antes de tomar decisiones basadas en su
        contenido se debe comunicar con el Líder del Equipo de Inocuidad de los Alimentos para
        verificar que su copia no controlada sigue vigente.
      </p>
      <p className="hoja-legal-sistema">SMG | {datos.sistema ?? SISTEMA_POR_DEFECTO}</p>
      <p className="hoja-legal-confidencial">Información Confidencial de la Organización</p>
      <p className="hoja-legal-sello">{leyenda(datos)}</p>
    </footer>
  );
}

function PieDeFirmas({ firmas }: { firmas: FirmaDeHoja[] }) {
  // Ruta corta (2026-10-29): sin revisor, el pie lleva dos columnas, Elaboró y Aprobó.
  const etapas = ETAPAS.filter((e) => firmas.some((f) => f.etapa === e));
  return (
    <table className="hoja-firmas">
      <thead>
        <tr>
          {etapas.map((e) => (
            <th key={e}>{NOMBRE_ETAPA[e]}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {etapas.map((e) => {
            const f = firmas.find((x) => x.etapa === e);
            return (
              <td key={e}>
                {f?.firmadoEn ? (
                  <>
                    {f.imagenUrl && <img className="hoja-firma-imagen" src={f.imagenUrl} alt="" />}
                    <span className="hoja-firma-nombre">{f.nombre}</span>
                    {f.puesto && <span className="hoja-firma-puesto">{f.puesto}</span>}
                    <span className="hoja-firma-fecha">{diaMesYAnio(f.firmadoEn)}</span>
                    {f.idFirma && <span className="hoja-firma-id">ID de firma {f.idFirma}</span>}
                  </>
                ) : (
                  <>
                    {f?.nombre && <span className="hoja-firma-nombre">{f.nombre}</span>}
                    {f?.puesto && <span className="hoja-firma-puesto">{f.puesto}</span>}
                    <span className="hoja-firma-pendiente">Pendiente</span>
                  </>
                )}
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Cuerpo
// ---------------------------------------------------------------------------

function SeccionDeHoja({
  seccion,
  numero,
  datos,
  sinTitulo = false,
}: {
  seccion: Seccion;
  numero: number;
  datos: DatosDeHoja;
  sinTitulo?: boolean;
}) {
  return (
    <section className="hoja-seccion" data-bloque={seccion.attrs.id} data-n={numero}>
      {!sinTitulo && (
        <h1>
          <span className="hoja-n">{numero}.</span>{" "}
          {/* El título se puede marcar (fase 4): va en su propio tramo, con el id de la sección. */}
          <span data-titulo={seccion.attrs.id}>
            {enLinea(
              [{ type: "text", text: seccion.attrs.titulo }],
              resaltadosDe(datos.resaltados, seccion.attrs.id),
            )}
          </span>
        </h1>
      )}
      {seccion.attrs.clave === "actualizaciones" ? (
        <ControlDeActualizaciones historial={datos.historial ?? []} />
      ) : (
        seccion.content.map((b) => <BloqueDeHoja key={b.attrs.id} bloque={b} datos={datos} />)
      )}
    </section>
  );
}

/**
 * La sección que el sistema llena solo: la tabla de "Control de las actualizaciones" de los
 * documentos de SMG, con una fila por revisión. La columna "Página" queda vacía porque el
 * historial no guarda páginas (el diff por bloques de la fase 4 la reemplaza con creces).
 */
function ControlDeActualizaciones({ historial }: { historial: EntradaDeHistorial[] }) {
  return (
    <table className="hoja-actualizaciones">
      <thead>
        <tr>
          <th>V</th>
          <th>Nombre del solicitante del cambio</th>
          <th>Puesto</th>
          <th>Fecha</th>
          <th>Página</th>
          <th>Descripción del cambio</th>
        </tr>
      </thead>
      <tbody>
        {historial.length === 0 ? (
          <tr>
            <td colSpan={6} className="hoja-vacio">
              Sin actualizaciones registradas.
            </td>
          </tr>
        ) : (
          historial.map((h) => (
            <tr key={h.revision}>
              <td className="hoja-centrado">{formatearRevision(h.revision)}</td>
              <td>{h.quien}</td>
              <td>{h.puesto ?? ""}</td>
              <td className="hoja-centrado">{h.fecha ? diaMesYAnio(h.fecha) : ""}</td>
              <td />
              <td>{h.descripcion}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Fechas, siempre en la zona de Honduras
// ---------------------------------------------------------------------------

/**
 * Los nombres van en tablas propias, no por `Intl`: la ICU de cada motor abrevia distinto
 * ("sept" en unas, "sep." en otras), y la vista previa en el navegador y el PDF del worker tienen
 * que decir exactamente lo mismo (ADR-03). `Intl` solo sirve para caer en el día de Honduras
 * (`diaLocal`).
 */
const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const MESES_CORTOS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

/** "Marzo 25": mes con nombre y año en dos cifras, como la casilla Fecha de los documentos de SMG. */
function mesYAnio(fecha: Date): string {
  const { mes, anio } = diaLocal(fecha);
  return `${MESES[mes - 1]} ${String(anio % 100).padStart(2, "0")}`;
}

/** "3 sep 2026", para las firmas y el historial. */
function diaMesYAnio(fecha: Date): string {
  const { dia, mes, anio } = diaLocal(fecha);
  return `${dia} ${MESES_CORTOS[mes - 1]} ${anio}`;
}
